import { registerPlugin } from "@capacitor/core";
import { isNativeApp } from "../native/capacitor";

const KEY_PREFIX = "device_local_auth_v1";
const enabledKey = (userId) => `${KEY_PREFIX}:enabled:${userId}`;
const NativeBiometric = registerPlugin("NativeBiometric");

let lastPasskeyError = "";

const supportInstallHint =
  "Biometric plugin is not installed in this app build. Run: npm install --legacy-peer-deps capacitor-native-biometric && npx cap sync";

const mapBiometricError = (error) => {
  const msg = String(error?.message || error || "");
  const lower = msg.toLowerCase();
  if (
    lower.includes("unimplemented") ||
    lower.includes("not implemented") ||
    lower.includes("nativebiometric")
  ) {
    return supportInstallHint;
  }
  if (lower.includes("biometry not available")) {
    return "Face ID/Touch ID is not available on this device.";
  }
  if (lower.includes("not enrolled")) {
    return "No Face ID/Touch ID is enrolled on this device.";
  }
  if (lower.includes("passcode")) {
    return "Set a device passcode before enabling Face ID/Touch ID unlock.";
  }
  return msg || "Biometric authentication failed.";
};

export const isPasskeySupported = () =>
  typeof window !== "undefined" && isNativeApp();

export const getLastPasskeyError = () => lastPasskeyError || "";

export const getPasskeySupportIssue = async () => {
  if (!isPasskeySupported()) {
    return "Face ID/Touch ID unlock is available only in the mobile app.";
  }
  try {
    const status = await NativeBiometric.isAvailable();
    if (!status?.isAvailable) {
      return "Face ID/Touch ID is not available on this device.";
    }
  } catch (error) {
    return mapBiometricError(error);
  }
  return "";
};

export const isPasskeyEnabledForUser = (userId) => {
  if (!userId) return false;
  return localStorage.getItem(enabledKey(userId)) === "1";
};

export const setPasskeyEnabledForUser = (userId, enabled) => {
  if (!userId) return;
  localStorage.setItem(enabledKey(userId), enabled ? "1" : "0");
};

export const clearPasskeyForUser = (userId) => {
  if (!userId) return;
  localStorage.removeItem(enabledKey(userId));
};

export const enrollLocalPasskey = async ({ userId, userName }) => {
  lastPasskeyError = "";
  if (!userId) {
    lastPasskeyError = "Missing user id.";
    return false;
  }
  const issue = await getPasskeySupportIssue();
  if (issue) {
    lastPasskeyError = issue;
    return false;
  }
  try {
    await NativeBiometric.verifyIdentity({
      reason: "Enable Face ID/Touch ID unlock for PaiCoach",
      title: "Enable Biometric Unlock",
      subtitle: String(userName || "PaiCoach"),
      description: "Confirm your identity to enable biometric unlock.",
    });
    setPasskeyEnabledForUser(userId, true);
    return true;
  } catch (error) {
    lastPasskeyError = mapBiometricError(error);
    return false;
  }
};

export const verifyLocalPasskey = async ({ userId }) => {
  if (!isPasskeySupported() || !userId || !isPasskeyEnabledForUser(userId)) return false;
  try {
    await NativeBiometric.verifyIdentity({
      reason: "Unlock PaiCoach",
      title: "Face ID / Touch ID",
      subtitle: "Authenticate to continue",
      description: "Use Face ID or Touch ID to unlock your account.",
    });
    return true;
  } catch (error) {
    lastPasskeyError = mapBiometricError(error);
    return false;
  }
};
